<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back to dashboard">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0">
            <i class="mdi mdi-account-injury me-2"></i>{{ t('patient.title','Patients') }}
          </h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-primary" @click="openAdd">
            <i class="mdi mdi-account-plus me-1"></i>{{ t('qa.register_patient','Register Patient') }}
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="d-flex flex-wrap gap-2 mb-3">
        <div class="hms-search-wrap">
          <i class="mdi mdi-magnify"></i>
          <input
            class="hms-search"
            v-model="search"
            placeholder="Search name, patient no, phone, ID..."
            @input="onSearchInput"
          />
        </div>
        <select class="hms-input hms-filter" v-model="filterStatus" @change="loadPatients">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deceased">Deceased</option>
          <option value="transferred">Transferred</option>
          <option value="">All</option>
        </select>
        <select class="hms-input hms-filter" v-model="filterGender" @change="loadPatients">
          <option value="">All Genders</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>
        <select class="hms-input hms-filter" v-model.number="pageSize" @change="onPageSizeChange">
          <option :value="50">50 / page</option>
          <option :value="100">100 / page</option>
          <option :value="200">200 / page</option>
        </select>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="hms-spinner">
        <div class="spinner-border"></div>
      </div>

      <!-- Patient list -->
      <div v-else class="hms-card">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="hms-muted">{{ filteredPatients.length }} {{ filteredPatients.length === 1 ? 'patient' : 'patients' }}</span>
          <span v-if="search" class="hms-muted-sm">
            <i class="mdi mdi-information-outline me-1"></i>Search results
          </span>
        </div>

        <div v-if="filteredPatients.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead>
              <tr>
                <th style="width:60px;">#</th>
                <th>Patient</th>
                <th>Patient No</th>
                <th>Age / DOB</th>
                <th>Gender</th>
                <th>Phone</th>
                <th>Insurance</th>
                <th>Status</th>
                <th>Queue</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, pi) in pagedPatients" :key="p.patient_id" class="hms-patient-row" @click="$router.push('/patients/' + p.patient_id + '/chart')">
                <td class="hms-muted">{{ ((page - 1) * pageSize) + pi + 1 }}</td>
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <div class="hms-avatar">{{ initials(p) }}</div>
                    <div>
                      <div class="hms-patient-name">
                        {{ fullName(p) }}
                        <button class="hms-copy-btn" @click.stop="copyText(fullName(p), 'Name')" title="Copy name"><i class="mdi mdi-content-copy"></i></button>
                      </div>
                      <div class="hms-muted-sm" v-if="p.national_id">
                        ID: {{ p.national_id }}
                        <button class="hms-copy-btn" @click.stop="copyText(p.national_id, 'National ID')" title="Copy National ID"><i class="mdi mdi-content-copy"></i></button>
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="hms-copyable">
                    <span class="hms-mono">{{ p.patient_no }}</span>
                    <button class="hms-copy-btn" @click.stop="copyText(p.patient_no, 'Patient No')" title="Copy patient no"><i class="mdi mdi-content-copy"></i></button>
                  </span>
                </td>
                <td>
                  <div>{{ ageFromDob(p.dob) }} yrs</div>
                  <div class="hms-muted-sm">{{ formatDate(p.dob) }}</div>
                </td>
                <td>{{ p.gender ? capitalize(p.gender) : '-' }}</td>
                <td>
                  <span class="hms-copyable" v-if="p.phone">
                    {{ p.phone }}
                    <button class="hms-copy-btn" @click.stop="copyText(p.phone, 'Phone')" title="Copy phone"><i class="mdi mdi-content-copy"></i></button>
                  </span>
                  <span v-else>-</span>
                </td>
                <td>
                  <span v-if="p.insurance_provider" class="hms-badge hms-badge-info">{{ p.insurance_provider }}</span>
                  <span v-else class="hms-badge hms-badge-neutral"><i class="mdi mdi-cash"></i> Cash</span>
                </td>
                <td>
                  <span class="hms-badge" :class="statusBadgeClass(p.status)">{{ p.status }}</span>
                </td>
                <td class="text-end" @click.stop>
                  <span v-if="p.active_ticket_id" class="hms-badge hms-badge-success" :title="'Queue ' + p.active_queue_code">
                    <i class="mdi mdi-check-circle"></i> {{ p.active_queue_code || 'Queued' }}
                  </span>
                  <button v-else class="hms-btn hms-btn-sm hms-btn-primary" @click="openQueueDialog(p)" title="Queue this patient">
                    <i class="mdi mdi-account-arrow-right"></i> Queue
                  </button>
                </td>
                <td class="text-end hms-muted-sm">
                  <i class="mdi mdi-chevron-right"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="hms-empty">
          <i class="mdi mdi-account-search-outline"></i>
          <div v-if="search">No patients matched "{{ search }}"</div>
          <div v-else>No patients yet — click <strong>Register Patient</strong> to add one.</div>
        </div>

        <!-- Pagination -->
        <div v-if="filteredPatients.length > pageSize" class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 hms-table-footer">
          <div class="hms-muted-sm">
            Showing <strong class="hms-text">{{ ((page - 1) * pageSize) + 1 }}</strong>
            –<strong class="hms-text">{{ Math.min(page * pageSize, filteredPatients.length) }}</strong>
            of <strong class="hms-text">{{ filteredPatients.length }}</strong>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button class="hms-btn hms-btn-sm" :disabled="page <= 1" @click="page = 1">«</button>
            <button class="hms-btn hms-btn-sm" :disabled="page <= 1" @click="page--">‹ Prev</button>
            <span class="hms-text-sm fw-bold mx-2">Page {{ page }} / {{ totalPages }}</span>
            <button class="hms-btn hms-btn-sm" :disabled="page >= totalPages" @click="page++">Next ›</button>
            <button class="hms-btn hms-btn-sm" :disabled="page >= totalPages" @click="page = totalPages">»</button>
          </div>
        </div>
      </div>

      <!-- ============ ADD / EDIT MODAL ============ -->
      <!-- click-outside no longer closes the form (FB#2-15) — only Cancel or X button does -->
      <div class="hms-modal-overlay" v-if="showForm">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span>
              <i class="mdi" :class="editing ? 'mdi-pencil' : 'mdi-account-plus'"></i>
              {{ editing ? 'Edit Patient' : 'Register Patient' }}
            </span>
            <button class="hms-modal-close" @click="closeForm">&times;</button>
          </div>

          <div v-if="formError" class="hms-alert hms-alert-danger">
            <i class="mdi mdi-alert-circle me-1"></i>{{ formError }}
          </div>
          <div v-if="formSuccess" class="hms-alert hms-alert-success">
            <i class="mdi mdi-check-circle me-1"></i>{{ formSuccess }}
          </div>

          <!-- Section: Basic info -->
          <div class="hms-section-label mt-2">{{ t('patient.section_identity','IDENTITY') }}</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">{{ t('patient.first_name','First Name') }} *</label>
              <input class="hms-input" :class="{'hms-invalid': invalid.first_name}" v-model="form.first_name" placeholder="John" />
            </div>
            <div class="col-md-6"><label class="hms-label">{{ t('patient.last_name','Last Name') }} *</label>
              <input class="hms-input" :class="{'hms-invalid': invalid.last_name}" v-model="form.last_name" placeholder="Doe" />
            </div>
            <div class="col-md-6"><label class="hms-label">{{ t('patient.other_names','Other Names') }}</label>
              <input class="hms-input" v-model="form.other_names" placeholder="Optional" />
            </div>
            <div class="col-md-3"><label class="hms-label">{{ t('patient.dob','Date of Birth') }} *</label>
              <input type="date" class="hms-input" :class="{'hms-invalid': invalid.dob}" v-model="form.dob" :max="todayIso" />
            </div>
            <div class="col-md-2"><label class="hms-label">
                {{ t('patient.age','Age (yrs)') }}
                <span v-if="isMinor" class="hms-badge hms-badge-warning ms-1" title="Under 18 — Parent/Guardian required">MINOR</span>
              </label>
              <input type="number" class="hms-input" v-model.number="displayAge" min="0" max="130" placeholder="—" title="Type age to auto-fill DOB" />
            </div>
            <div class="col-md-3"><label class="hms-label">{{ t('patient.gender','Gender') }} *</label>
              <select class="hms-input" :class="{'hms-invalid': invalid.gender}" v-model="form.gender">
                <option value="">Select...</option>
                <option value="male">{{ t('patient.male','Male') }}</option>
                <option value="female">{{ t('patient.female','Female') }}</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <!-- Section: Contact -->
          <div class="hms-section-label">CONTACT</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">{{ t('patient.phone','Phone') }} *</label>
              <input class="hms-input" :class="{'hms-invalid': invalid.phone}" v-model="form.phone" placeholder="0712345678" />
              <div class="hms-muted-sm mt-1">
                <i class="mdi mdi-cellphone-message"></i>
                Patient portal login credentials will be sent here via SMS{{ '' }}<span v-if="form.email"> and email</span>.
                <span v-if="isMinor">If the child has no phone, enter the parent/guardian's phone.</span>
              </div>
            </div>
            <div class="col-md-6"><label class="hms-label">{{ t('patient.email','Email') }}</label>
              <input type="email" class="hms-input" v-model="form.email" placeholder="patient@example.com" />
            </div>
            <div class="col-md-6"><label class="hms-label">Identification Document Type</label>
              <select class="hms-input" v-model="form.identification_type" @change="onIdTypeChange">
                <option v-for="d in idDocTypes" :key="d.value" :value="d.value">{{ d.label }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">
                {{ currentIdDocLabel }}
                <span v-if="isMinor && form.identification_type === 'birth_certificate'" class="hms-muted-sm">(Kenyan minors don't have a National ID)</span>
              </label>
              <input class="hms-input" v-model="form.national_id" :placeholder="currentIdDocPlaceholder" />
            </div>
            <div class="col-md-6"><label class="hms-label">Nationality</label>
              <select class="hms-input" v-model="form.country">
                <option value="">— Select nationality —</option>
                <option v-for="c in countries" :key="c" :value="c" :disabled="c.startsWith('—')">{{ c }}</option>
              </select>
            </div>
            <div class="col-md-6" v-if="(form.country || '').toLowerCase() === 'kenya'"><label class="hms-label">{{ t('patient.county','County') }}</label>
              <select class="hms-input" v-model.number="form.county_id" @change="onCountyChange">
                <option :value="null">— Select county —</option>
                <option v-for="c in counties" :key="c.county_id" :value="c.county_id">{{ c.county_name }}</option>
              </select>
            </div>
            <div class="col-md-6" v-if="form.county_id"><label class="hms-label">{{ t('patient.sub_county','Sub-county') }}</label>
              <select class="hms-input" v-model.number="form.sub_county_id">
                <option :value="null">— Select sub-county —</option>
                <option v-for="s in subCounties" :key="s.sub_county_id" :value="s.sub_county_id">{{ s.sub_county_name }}</option>
              </select>
            </div>
            <div class="col-12">
              <label class="hms-label">Address &amp; Location</label>
              <div class="d-flex gap-2 flex-wrap">
                <input class="hms-input" style="flex:1; min-width:240px;" v-model="addressQuery"
                       @keydown.enter.prevent="geocodeAddress"
                       @input="onAddressInput"
                       placeholder="Search address — town, estate, landmark…" />
                <button type="button" class="hms-btn hms-btn-blue" @click="geocodeAddress" :disabled="addressBusy">
                  <i class="mdi" :class="addressBusy ? 'mdi-loading mdi-spin' : 'mdi-magnify'"></i>
                  {{ addressBusy ? 'Searching…' : 'Search' }}
                </button>
                <button type="button" class="hms-btn hms-btn-blue" @click="useMyLocation" :disabled="addressBusy" title="Use current GPS">
                  <i class="mdi mdi-crosshairs-gps"></i> Use my GPS
                </button>
              </div>
              <div v-if="addressSuggest.length" class="hms-suggest mt-1">
                <div v-for="(s, i) in addressSuggest" :key="i" class="hms-suggest-item" @click="pickAddressSuggestion(s)">
                  <i class="mdi mdi-map-marker-outline me-1"></i>{{ s.display_name }}
                </div>
              </div>
              <div ref="addressMap" class="hms-leaflet-map mt-2"></div>
              <div class="hms-muted-sm mt-1">
                <span v-if="form.latitude && form.longitude">
                  <i class="mdi mdi-crosshairs-gps me-1"></i>
                  Captured: {{ Number(form.latitude).toFixed(5) }}, {{ Number(form.longitude).toFixed(5) }}
                </span>
                <span v-else>Drag the pin to fine-tune the exact location.</span>
              </div>
              <input type="text" class="hms-input mt-2" v-model="form.address" placeholder="Refine address text (auto-filled from search)" />
            </div>
          </div>

          <!-- Section: Medical — hidden on NEW registration (HMIS_Issues: allergies + blood group are captured at Triage) -->
          <div class="hms-section-label" v-if="editing">{{ t('patient.section_medical','MEDICAL') }}</div>
          <div class="row g-3 mb-3" v-if="editing">
            <div class="col-md-4"><label class="hms-label">Blood Group</label>
              <select class="hms-input" v-model="form.blood_group">
                <option value="unknown">Unknown</option>
                <option v-for="bg in bloodGroups" :key="bg" :value="bg">{{ bg }}</option>
              </select>
            </div>
            <div class="col-md-8"><label class="hms-label">Allergies</label>
              <div class="hms-chip-input">
                <span v-for="(t, i) in allergyTags" :key="i" class="hms-chip">
                  {{ t }}
                  <button type="button" class="hms-chip-x" @click="removeAllergy(i)" title="Remove">&times;</button>
                </span>
                <input class="hms-chip-text" list="hms-allergies-list"
                       v-model="allergyInput"
                       @keydown.enter.prevent="addAllergyFromInput"
                       @keydown.,.prevent="addAllergyFromInput"
                       @keydown.tab="addAllergyFromInput"
                       @change="addAllergyFromInput"
                       placeholder="Type or pick — press Enter to add another…" />
              </div>
              <datalist id="hms-allergies-list">
                <option v-for="a in commonAllergies" :key="a" :value="a"></option>
              </datalist>
              <div class="hms-muted-sm mt-1">Pick from the list or type your own. Press Enter (or comma) to add each one. Click × on a chip to remove.</div>
            </div>
            <div class="col-12" v-if="editing"><label class="hms-label">Chronic Conditions</label>
              <input class="hms-input" v-model="form.chronic_conditions" placeholder="e.g. Diabetes, Hypertension" />
            </div>
          </div>

          <!-- Section: Next of Kin (or Parent/Guardian if minor) -->
          <div class="hms-section-label">
            {{ isMinor ? 'PARENT / GUARDIAN' : t('patient.section_nok','NEXT OF KIN') }}
            <span v-if="isMinor" class="hms-badge hms-badge-warning ms-2">Required for minors</span>
          </div>
          <div v-if="isMinor" class="hms-muted-sm mb-2">
            The parent or legal guardian is the accountable adult for consent, contact, and billing.
            Their phone is used for SMS reminders and vaccine schedules.
          </div>
          <div class="row g-3 mb-3">
            <div class="col-md-5"><label class="hms-label">
                {{ isMinor ? "Parent / Guardian's Full Name *" : 'Name' }}
              </label>
              <input class="hms-input" :class="{'hms-invalid': invalid.next_of_kin_name}" v-model="form.next_of_kin_name" placeholder="Full name" />
            </div>
            <div class="col-md-4"><label class="hms-label">
                {{ isMinor ? 'Phone *' : 'Phone' }}
              </label>
              <input class="hms-input" :class="{'hms-invalid': invalid.next_of_kin_phone}" v-model="form.next_of_kin_phone" placeholder="0712345678" />
            </div>
            <div class="col-md-3"><label class="hms-label">
                {{ isMinor ? 'Relationship *' : 'Relationship' }}
              </label>
              <select class="hms-input" :class="{'hms-invalid': invalid.next_of_kin_relation}" v-model="form.next_of_kin_relation">
                <option value="">Select...</option>
                <template v-if="isMinor">
                  <option value="Mother">Mother</option>
                  <option value="Father">Father</option>
                  <option value="Guardian">Legal Guardian</option>
                  <option value="Grandparent">Grandparent</option>
                  <option value="Aunt/Uncle">Aunt / Uncle</option>
                  <option value="Sibling">Sibling (adult)</option>
                  <option value="Foster">Foster Parent</option>
                  <option value="Other">Other</option>
                </template>
                <template v-else>
                  <option value="Spouse">Spouse</option>
                  <option value="Parent">Parent</option>
                  <option value="Child">Child</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Friend">Friend</option>
                  <option value="Other">Other</option>
                </template>
              </select>
            </div>
            <div class="col-md-6" v-if="form.next_of_kin_relation === 'Other'">
              <label class="hms-label">Describe the relationship *</label>
              <input class="hms-input" :class="{'hms-invalid': invalid.nok_other}" v-model="nokOtherText" placeholder="e.g. Neighbour, Employer, Church elder…" />
            </div>
          </div>

          <!-- Section: Payment method -->
          <div class="hms-section-label">{{ t('patient.section_payment','PAYMENT METHOD') }}</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Payment Method</label>
              <select class="hms-input" v-model="form.payment_method" @change="onPaymentMethodChange">
                <option value="insurance">Insurance</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
            <template v-if="form.payment_method === 'insurance'">
              <div class="col-md-6"><label class="hms-label">Insurance Provider</label>
                <select class="hms-input" v-model="form.insurance_provider">
                  <option value="">— Select provider —</option>
                  <option v-for="p in insuranceProviders" :key="p" :value="p">{{ p }}</option>
                </select>
              </div>
              <div class="col-md-6"><label class="hms-label">Policy / Member No</label>
                <input class="hms-input" v-model="form.insurance_number" placeholder="Member number" />
                <div class="hms-muted-sm mt-1">Live member lookup &amp; validation activates with the facility's SHA / insurer EDI credentials.</div>
              </div>
            </template>
          </div>

          <!-- Section: Source -->
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Registration Source</label>
              <select class="hms-input" v-model="form.registration_source">
                <option value="walk_in">Walk-in</option>
                <option value="referral">Referral (from another facility)</option>
                <option value="emergency">Emergency</option>
                <option value="online">Online</option>
                <option value="deceased">Deceased (brought in dead / mortuary)</option>
              </select>
            </div>
          </div>

          <!-- Section: Referral Source (only when source = referral) -->
          <div v-if="form.registration_source === 'referral'">
            <div class="hms-alert hms-alert-amber mb-3">
              <i class="mdi mdi-alert-circle-outline me-1"></i>
              <strong>Referral traceability</strong> — capture every detail you can. This record is used if the referring facility delayed unsafely (e.g. late transfer of a critically-ill patient).
            </div>

            <div class="hms-section-label">REFERRING FACILITY</div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="hms-label">Facility Name *</label>
                <input class="hms-input" :class="{'hms-invalid': invalid.referral_source_facility}" v-model="form.referral_source_facility" placeholder="e.g. Loita Sub-County Hospital / Karen Hospital" />
              </div>
              <div class="col-md-3"><label class="hms-label">Facility Type *</label>
                <select class="hms-input" :class="{'hms-invalid': invalid.referral_source_type}" v-model="form.referral_source_type">
                  <option value="">Select…</option>
                  <option value="public">Public (MoH)</option>
                  <option value="private">Private</option>
                  <option value="mission">Mission / Faith-based</option>
                  <option value="ngo">NGO</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <div class="col-md-3"><label class="hms-label">KEPH Level</label>
                <select class="hms-input" v-model="form.referral_source_level">
                  <option value="">—</option>
                  <option value="L1">L1 — Community Unit</option>
                  <option value="L2">L2 — Dispensary</option>
                  <option value="L3">L3 — Health Centre</option>
                  <option value="L4">L4 — Sub-County / Primary Hospital</option>
                  <option value="L5">L5 — County Referral</option>
                  <option value="L6">L6 — National Referral</option>
                  <option value="private_clinic">Private clinic</option>
                  <option value="private_hospital">Private hospital</option>
                </select>
              </div>
              <div class="col-md-6"><label class="hms-label">County</label>
                <input class="hms-input" v-model="form.referral_source_county" placeholder="e.g. Narok / Nairobi / Kajiado" />
              </div>
              <div class="col-md-3"><label class="hms-label">Mode of Transport</label>
                <select class="hms-input" v-model="form.referral_source_transport">
                  <option value="">—</option>
                  <option value="ambulance">Ambulance</option>
                  <option value="private_car">Private car</option>
                  <option value="public_transport">Public transport</option>
                  <option value="walked">Walked</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div class="col-md-3"><label class="hms-check mt-4">
                <input type="checkbox" v-model="form.referral_source_documentation" :true-value="1" :false-value="0" />
                Patient brought referral letter / file
              </label></div>
            </div>

            <div class="hms-section-label">REFERRING CLINICIAN</div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="hms-label">Contact Name</label>
                <input class="hms-input" v-model="form.referral_source_contact_name" placeholder="Dr / Nurse name at the referring facility" />
              </div>
              <div class="col-md-6"><label class="hms-label">Contact Phone</label>
                <input class="hms-input" v-model="form.referral_source_contact_phone" placeholder="07XX XXX XXX" />
              </div>
            </div>

            <div class="hms-section-label">CLINICAL HAND-OVER</div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="hms-label">Reason for Referral *</label>
                <textarea class="hms-input" rows="2" v-model="form.referral_source_reason" placeholder="Primary clinical reason"></textarea>
              </div>
              <div class="col-md-3"><label class="hms-label">Arrival Condition *</label>
                <select class="hms-input" :class="{'hms-invalid': invalid.referral_arrival_condition}" v-model="form.referral_arrival_condition">
                  <option value="">Select…</option>
                  <option value="stable">Stable</option>
                  <option value="critical">Critical</option>
                  <option value="moribund">Moribund (near death)</option>
                  <option value="dead_on_arrival">Dead on arrival</option>
                </select>
              </div>
              <div class="col-md-3"><label class="hms-label">Date/Time Referred</label>
                <input type="datetime-local" class="hms-input" v-model="form.referral_source_date" />
                <div class="hms-muted-sm mt-1">When did they leave the source facility?</div>
              </div>
              <div class="col-12"><label class="hms-label">Hand-over Notes</label>
                <textarea class="hms-input" rows="2" v-model="form.referral_source_notes" placeholder="Treatment already given, observations, anything unusual about the referral process"></textarea>
              </div>
            </div>
          </div>

          <div v-if="formError" class="hms-alert hms-alert-danger mt-3" ref="submitError">
            <i class="mdi mdi-alert-circle me-1"></i>{{ formError }}
          </div>
          <div v-if="formSuccess" class="hms-alert hms-alert-success mt-3">
            <i class="mdi mdi-check-circle me-1"></i>{{ formSuccess }}
          </div>
          <button class="hms-btn hms-btn-primary w-100 mt-2" @click="savePatient" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (editing ? 'Save Changes' : 'Register Patient') }}
          </button>
        </div>
      </div>

      <!-- ============ POST-REGISTRATION PROMPT (HMIS_Issues: action prompt instead of silent redirect) ============ -->
      <div class="hms-modal-overlay" v-if="postRegister">
        <div class="hms-modal" style="max-width:440px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-check-circle me-2" style="color:#16a34a;"></i>Patient Registered</span>
            <button class="hms-modal-close" @click="postRegister=null">&times;</button>
          </div>
          <p class="mb-1"><strong>{{ postRegister.name }}</strong> has been registered successfully.</p>
          <p class="hms-muted-sm" v-if="postRegister.patient_no">Patient No: <strong>{{ postRegister.patient_no }}</strong></p>
          <p class="hms-muted-sm mb-3">What would you like to do next?</p>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-primary flex-fill" @click="proceedToTriage" :disabled="queueing">
              <span v-if="queueing" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-heart-pulse me-1"></i>Proceed to Triage
            </button>
            <button class="hms-btn flex-fill" @click="postRegister=null">
              <i class="mdi mdi-arrow-left me-1"></i>Back to Register
            </button>
          </div>
        </div>
      </div>

      <!-- ============ VIEW PATIENT MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showView" @click.self="showView=false">
        <div class="hms-modal" v-if="viewingPatient">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-account-eye me-2"></i>{{ fullName(viewingPatient) }}</span>
            <button class="hms-modal-close" @click="showView=false">&times;</button>
          </div>

          <div class="hms-view-header">
            <div class="hms-avatar hms-avatar-lg">{{ initials(viewingPatient) }}</div>
            <div class="ms-3">
              <div class="hms-view-name">{{ fullName(viewingPatient) }}</div>
              <div class="hms-mono hms-muted-sm">{{ viewingPatient.patient_no }}</div>
              <div class="mt-1">
                <span class="hms-badge" :class="statusBadgeClass(viewingPatient.status)">{{ viewingPatient.status }}</span>
                <span class="hms-badge hms-badge-neutral ms-1">{{ ageFromDob(viewingPatient.dob) }} yrs</span>
                <span class="hms-badge hms-badge-neutral ms-1" v-if="viewingPatient.gender">{{ capitalize(viewingPatient.gender) }}</span>
                <span class="hms-badge hms-badge-warning ms-1" v-if="viewingPatient.blood_group && viewingPatient.blood_group !== 'unknown'">{{ viewingPatient.blood_group }}</span>
              </div>
            </div>
          </div>

          <div class="hms-section-label mt-3">CONTACT</div>
          <div class="hms-kv">
            <div><span class="hms-kv-key">Phone</span><span>{{ viewingPatient.phone || '—' }}</span></div>
            <div><span class="hms-kv-key">Email</span><span>{{ viewingPatient.email || '—' }}</span></div>
            <div><span class="hms-kv-key">{{ viewingIdDocLabel }}</span><span>{{ viewingPatient.national_id || '—' }}</span></div>
            <div><span class="hms-kv-key">Nationality</span><span>{{ viewingPatient.country || '—' }}</span></div>
            <div><span class="hms-kv-key">Address</span><span>{{ viewingPatient.address || '—' }}</span></div>
          </div>

          <!-- Sharon F#2 B30: registry view should NOT show clinical details (ART, chronic disease).
               Allergies + blood group are kept as a single safety strip (already in the header).
               Chronic conditions and other clinical context are accessed via the patient chart
               (Consultation / Triage) where the clinical-data RBAC + consent rules apply. -->
          <div class="hms-section-label mt-3">SAFETY FLAGS</div>
          <div class="hms-kv">
            <div><span class="hms-kv-key">Allergies</span><span>{{ viewingPatient.allergies || '—' }}</span></div>
            <div><span class="hms-kv-key">Blood group</span><span>{{ viewingPatient.blood_group || '—' }}</span></div>
          </div>
          <div class="hms-muted-sm mt-1" style="font-style: italic;">
            <i class="mdi mdi-information-outline"></i>
            Clinical details (chronic conditions, ART, history) are visible in the patient chart at consultation level — not the registry view.
          </div>

          <div class="hms-section-label mt-3">REGISTRATION</div>
          <div class="hms-kv">
            <div><span class="hms-kv-key">Registered by</span><span>{{ viewingPatient.registered_by_name || '—' }}</span></div>
            <div><span class="hms-kv-key">Registered on</span><span>{{ viewingPatient.created_at || '—' }}</span></div>
            <div><span class="hms-kv-key">Source</span><span>{{ viewingPatient.registration_source || '—' }}</span></div>
          </div>

          <div class="hms-section-label mt-3">NEXT OF KIN</div>
          <div class="hms-kv">
            <div><span class="hms-kv-key">Name</span><span>{{ viewingPatient.next_of_kin_name || '—' }}</span></div>
            <div><span class="hms-kv-key">Phone</span><span>{{ viewingPatient.next_of_kin_phone || '—' }}</span></div>
            <div><span class="hms-kv-key">Relationship</span><span>{{ viewingPatient.next_of_kin_relation || '—' }}</span></div>
          </div>

          <div class="hms-section-label mt-3">INSURANCE</div>
          <div class="hms-kv">
            <div><span class="hms-kv-key">Provider</span><span>{{ viewingPatient.insurance_provider || '—' }}</span></div>
            <div><span class="hms-kv-key">Policy / Member No</span><span>{{ viewingPatient.insurance_number || '—' }}</span></div>
          </div>

          <!-- ============ BILLING SUMMARY ============ -->
          <div class="hms-section-label mt-3">BILLING &amp; INVOICES</div>
          <div v-if="billingLoading" class="hms-spinner py-2"><div class="spinner-border spinner-border-sm"></div></div>
          <div v-else-if="billingSummary">
            <div class="hms-billing-stats">
              <div class="hms-bs">
                <div class="hms-bs-num">{{ billingSummary.totals.invoice_count }}</div>
                <div class="hms-bs-lbl">Invoices</div>
              </div>
              <div class="hms-bs">
                <div class="hms-bs-num">KES {{ formatMoney(billingSummary.totals.total_billed) }}</div>
                <div class="hms-bs-lbl">Total billed</div>
              </div>
              <div class="hms-bs">
                <div class="hms-bs-num hms-text-success">KES {{ formatMoney(billingSummary.totals.total_paid) }}</div>
                <div class="hms-bs-lbl">Paid</div>
              </div>
              <div class="hms-bs">
                <div class="hms-bs-num" :class="parseFloat(billingSummary.totals.total_outstanding) > 0 ? 'hms-text-danger' : 'hms-text-success'">
                  KES {{ formatMoney(billingSummary.totals.total_outstanding) }}
                </div>
                <div class="hms-bs-lbl">Outstanding</div>
              </div>
            </div>

            <div v-if="billingSummary.invoices.length" style="overflow-x:auto; margin-top: 12px;">
              <table class="hms-bs-table">
                <thead>
                  <tr>
                    <th>Invoice</th><th>Date</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="inv in billingSummary.invoices" :key="inv.invoice_id">
                    <td><span class="hms-mono">{{ inv.invoice_number }}</span></td>
                    <td>{{ formatDate(inv.invoice_date) }}</td>
                    <td>KES {{ formatMoney(inv.total_amount) }}</td>
                    <td class="hms-text-success">KES {{ formatMoney(inv.paid_amount) }}</td>
                    <td :class="parseFloat(inv.balance) > 0 ? 'hms-text-danger fw-bold' : ''">KES {{ formatMoney(inv.balance) }}</td>
                    <td><span class="hms-badge" :class="payStatusBadge(inv.payment_status)">{{ inv.payment_status }}</span></td>
                    <td>
                      <a class="hms-btn-link" :href="payLinkUrl(inv.pay_token)" target="_blank" v-if="inv.pay_token && parseFloat(inv.balance) > 0" title="Open pay link">
                        <i class="mdi mdi-share-variant"></i> link
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="hms-muted-sm mt-2">No invoices for this patient yet.</div>
          </div>
          <div v-else class="hms-muted-sm">Billing data not loaded.</div>

          <div class="d-flex gap-2 mt-4 flex-wrap">
            <button class="hms-btn hms-btn-amber flex-fill" @click="showView=false; openEdit(viewingPatient)">
              <i class="mdi mdi-pencil me-1"></i>Edit
            </button>
            <button class="hms-btn hms-btn-green flex-fill" :disabled="resendingWelcome" @click="resendWelcome(viewingPatient)"
                    :title="(viewingPatient && (viewingPatient.phone || viewingPatient.email)) ? '' : 'No phone or email on file'">
              <i class="mdi" :class="resendingWelcome ? 'mdi-loading mdi-spin' : 'mdi-message-text-fast-outline'"></i>
              {{ resendingWelcome ? ' Sending…' : ' Resend Welcome SMS + Email' }}
            </button>
            <button class="hms-btn hms-btn-blue flex-fill" @click="showView=false">Close</button>
          </div>
        </div>
      </div>

      <!-- ============ DELETE CONFIRM ============ -->
      <div class="hms-modal-overlay" v-if="confirmingDelete" @click.self="confirmingDelete=null">
        <div class="hms-modal" style="max-width:420px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-alert-octagram me-2 text-danger"></i>Confirm Delete</span>
            <button class="hms-modal-close" @click="confirmingDelete=null">&times;</button>
          </div>
          <p class="hms-card-text">
            Soft-delete <strong>{{ fullName(confirmingDelete) }}</strong> ({{ confirmingDelete.patient_no }})?
            The record stays in the database with a deleted_at timestamp and can be recovered.
          </p>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-blue flex-fill" @click="confirmingDelete=null">Cancel</button>
            <button class="hms-btn hms-btn-red flex-fill" @click="doDelete" :disabled="deletingId">
              <span v-if="deletingId" class="spinner-border spinner-border-sm me-1"></span>Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>

      <!-- ============ QUEUE DIALOG (Feedback #3 #10) ============ -->
      <div v-if="queueDialog" class="hms-modal-overlay" @click.self="closeQueueDialog">
        <div class="hms-modal" style="max-width:460px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-account-arrow-right"></i> Queue patient</span>
            <button class="hms-modal-close" @click="closeQueueDialog">&times;</button>
          </div>
          <div class="mb-3" v-if="queueTarget">
            <div class="hms-muted-sm">Patient</div>
            <div class="fw-bold">{{ fullName(queueTarget) }} <span class="hms-muted-sm">· {{ queueTarget.patient_no }}</span></div>
          </div>
          <label class="hms-label">Queue to</label>
          <select class="hms-input" v-model="queueDestination">
            <option v-for="o in queueOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <div class="d-flex justify-content-end gap-2 mt-4">
            <button class="hms-btn" @click="closeQueueDialog">Cancel</button>
            <button class="hms-btn hms-btn-primary" :disabled="queueing" @click="confirmQueue">
              <span v-if="queueing"><span class="spinner-border spinner-border-sm me-1"></span> Queuing…</span>
              <span v-else><i class="mdi mdi-check"></i> Queue</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:          #f0f0f0;
  --hms-s:          #a0a0a0;
  --hms-m:          #808080;
  --hms-bg1:        #141414;
  --hms-bg2:        #0a0a0a;
  --hms-bg3:        #1e1e1e;
  --hms-bg4:        #0f0f0f;
  --hms-br:         #2a2a2a;
  --hms-input-br:   #333333;
  --hms-placeholder:#4a4a4a;
  --hms-primary:    #1a7fe8;
  --hms-primary-lt: rgba(26,127,232,0.12);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
}
html[data-theme="light"] {
  --hms-h:          #1a1a1a;
  --hms-s:          #555555;
  --hms-m:          #777777;
  --hms-bg1:        #ffffff;
  --hms-bg2:        #f4f6f9;
  --hms-bg3:        #eef1f5;
  --hms-bg4:        #ffffff;
  --hms-br:         #dee2e8;
  --hms-input-br:   #ced4da;
  --hms-placeholder:#aaaaaa;
  --hms-primary:    #0047AB;
  --hms-primary-lt: rgba(0,71,171,0.08);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
}
html[data-theme="light"] body,
html[data-theme="dark"] body {
  background: var(--hms-bg2) !important;
  color: var(--hms-h) !important;
}
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted     { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm  { color: var(--hms-s); font-size: 12px; }
.hms-text      { color: var(--hms-h); }
.hms-text-sm   { color: var(--hms-h); font-size: 13px; }
.hms-mono      { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; color: var(--hms-h); }

/* Filters */
.hms-search-wrap { position: relative; flex: 1; min-width: 260px; max-width: 380px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search {
  width: 100%;
  background: var(--hms-bg4);
  border: 1px solid var(--hms-input-br);
  border-radius: 8px;
  padding: 8px 14px 8px 36px;
  color: var(--hms-h);
  font-size: 14px;
  outline: none;
  transition: border-color .15s;
}
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input {
  width: 100%;
  background: var(--hms-bg4);
  border: 1px solid var(--hms-input-br);
  border-radius: 8px;
  padding: 8px 14px;
  color: var(--hms-h);
  font-size: 14px;
  outline: none;
  transition: border-color .15s;
}
.hms-input:focus { border-color: var(--hms-primary); }
.hms-invalid, .hms-invalid:focus { border-color: var(--hms-danger) !important; background: rgba(220,53,69,0.06); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }

/* Card */
.hms-card {
  background: var(--hms-bg1);
  border: 1px solid var(--hms-br);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 16px;
}

/* Spinner */
.hms-spinner { display: flex; justify-content: center; padding: 60px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }

/* Empty */
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

/* Table */
.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }
.hms-table-footer { border-top: 1px solid var(--hms-br); padding-top: 12px; }

/* Avatar */
.hms-avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: var(--hms-primary-lt);
  color: var(--hms-primary);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 12px;
  flex-shrink: 0;
}
.hms-avatar-lg { width: 56px; height: 56px; font-size: 18px; }
.hms-patient-name { color: var(--hms-h); font-weight: 600; font-size: 14px; }

/* Badges */
.hms-badge {
  padding: 3px 10px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  display: inline-block;
}
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

/* Buttons */
.hms-btn {
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue   { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber  { background: rgba(245,158,11,.15);  color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-red    { background: rgba(220,53,69,.15);   color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-red:hover:not(:disabled) { background: rgba(220,53,69,.25); }
.hms-btn-green  { background: var(--hms-success); color: #fff; }
.hms-btn-green:hover:not(:disabled) { filter: brightness(1.08); }

/* Modal */
.hms-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.6);
  z-index: 200;
  display: flex; align-items: flex-start; justify-content: center;
  padding: 40px 20px;
  overflow-y: auto;
}
.hms-modal {
  background: var(--hms-bg1);
  border: 1px solid var(--hms-br);
  border-radius: 16px;
  padding: 28px;
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  box-shadow: 0 24px 60px rgba(0,0,0,.3);
}
.hms-modal-title {
  color: var(--hms-h);
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.hms-modal-close {
  color: var(--hms-s);
  cursor: pointer;
  font-size: 26px;
  line-height: 1;
  background: none;
  border: none;
  padding: 0 4px;
}
.hms-modal-close:hover { color: var(--hms-h); }

/* Form bits */
.hms-section-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--hms-s);
  letter-spacing: 1.5px;
  margin-bottom: 8px;
}
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

/* Alerts */
.hms-alert {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 12px;
}
.hms-alert-danger  { background: rgba(220,53,69,.1);  color: var(--hms-danger);  border: 1px solid rgba(220,53,69,.3); }
.hms-alert-success { background: rgba(40,167,69,.1);  color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-alert-amber   { background: rgba(245,158,11,.10); color: #b45309;            border: 1px solid rgba(245,158,11,.35); }

.hms-leaflet-map   { width: 100%; height: 280px; border-radius: 10px; border: 1px solid var(--hms-br); overflow: hidden; }

.hms-chip-input    { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; padding: 6px 10px; min-height: 38px; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; }
.hms-chip-input:focus-within { border-color: var(--hms-primary); }
.hms-chip          { display: inline-flex; align-items: center; gap: 4px; background: var(--hms-primary-lt); color: var(--hms-primary); border-radius: 50px; padding: 3px 4px 3px 10px; font-size: 12px; font-weight: 600; }
.hms-chip-x        { background: transparent; border: none; color: inherit; font-size: 18px; line-height: 1; cursor: pointer; padding: 0 6px; opacity: 0.7; }
.hms-chip-x:hover  { opacity: 1; }
.hms-chip-text     { flex: 1; min-width: 140px; border: none; outline: none; background: transparent; color: var(--hms-h); font-size: 14px; padding: 4px; }

.hms-billing-stats { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.hms-bs            { flex: 1; min-width: 120px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 14px; }
.hms-bs-num        { font-size: 16px; font-weight: 800; color: var(--hms-primary); line-height: 1.2; }
.hms-bs-lbl        { font-size: 11px; color: var(--hms-s); margin-top: 2px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-patient-row   { cursor: pointer; }
.hms-patient-row:hover td { background: var(--hms-primary-lt); }

/* Copy-to-clipboard buttons */
.hms-copyable      { display: inline-flex; align-items: center; gap: 6px; }
.hms-copy-btn {
  background: transparent;
  border: none;
  color: var(--hms-s);
  cursor: pointer;
  padding: 2px 5px;
  margin-left: 2px;
  border-radius: 5px;
  font-size: 13px;
  line-height: 1;
  opacity: 0.5;
  transition: all .15s;
  vertical-align: middle;
}
.hms-copy-btn:hover { opacity: 1; color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-patient-row:hover .hms-copy-btn { opacity: 0.85; }
.hms-bs-table      { width: 100%; border-collapse: collapse; }
.hms-bs-table th, .hms-bs-table td { padding: 6px 8px; border-bottom: 1px solid var(--hms-br); font-size: 12px; text-align: left; }
.hms-bs-table th   { color: var(--hms-s); font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; }
.hms-btn-link      { color: var(--hms-primary); text-decoration: none; font-size: 12px; }
.hms-btn-link:hover{ text-decoration: underline; }
.hms-mono          { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-badge         { padding: 2px 8px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-amber   { background: rgba(245,158,11,.15); color: #f59e0b; }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-orange  { background: rgba(245,130,31,.15); color: #f5821f; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-s); }
.hms-suggest       { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 8px; max-height: 220px; overflow-y: auto; }
.hms-suggest-item  { padding: 8px 12px; font-size: 13px; cursor: pointer; border-bottom: 1px solid var(--hms-br); color: var(--hms-h); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }

/* View modal */
.hms-view-header { display: flex; align-items: center; padding: 8px 0; }
.hms-view-name { color: var(--hms-h); font-size: 18px; font-weight: 700; }
.hms-kv {
  background: var(--hms-bg2);
  border: 1px solid var(--hms-br);
  border-radius: 10px;
  padding: 12px 16px;
}
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 160px; font-weight: 600; }

.hms-card-text { color: var(--hms-m); font-size: 14px; line-height: 1.6; margin: 12px 0 18px; }

/* Toast */
.hms-toast {
  position: fixed;
  bottom: 24px; right: 24px;
  background: var(--hms-bg1);
  border: 1px solid var(--hms-success);
  color: var(--hms-h);
  padding: 12px 18px;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(40,167,69,.2);
  z-index: 1000;
  font-size: 13px;
  display: flex;
  align-items: center;
}
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

@media (max-width: 768px) {
  .hms-search-wrap { min-width: 100%; }
  .hms-modal { padding: 20px; }
  .hms-kv-key { flex: 0 0 130px; }
}
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";

export default {
  name: "PatientManagement",
  components: { Navigation },
  data() {
    return {
      loading:       true,
      processing:    false,
      patients:      [],
      search:        "",
      searchTimer:   null,
      i18n:          useI18n(),
      filterStatus:  "active",
      filterGender:  "",
      pageSize:      50,
      page:          1,

      showForm:       false,
      editing:        false,
      editingId:      null,
      formError:      "",
      formSuccess:    "",
      form:           this.emptyForm(),
      invalid:        {},              // per-field validation flags → red border (HMIS_Issues)
      nokOtherText:   "",              // free-text when Relationship = Other
      postRegister:   null,            // { patient_id, patient_no, name } → Proceed-to-Triage prompt

      showView:       false,
      viewingPatient: null,

      // Per-row Queue dialog (Feedback #3 #10)
      queueDialog:     false,
      queueTarget:     null,           // patient being queued
      queueDestination:'triage',       // default station
      queueOptions:    [
        { value: 'triage',       label: 'Triage' },
        { value: 'consultation', label: 'Consultation' },
        { value: 'lab',          label: 'Laboratory' },
        { value: 'radiology',    label: 'Radiology' },
        { value: 'pharmacy',     label: 'Pharmacy' },
        { value: 'billing',      label: 'Billing' },
        { value: 'admission',    label: 'Admission' }
      ],
      queueing:        false,

      confirmingDelete: null,
      deletingId:       null,

      toastMsg:    "",
      toastError:  false,
      toastTimer:  null,

      bloodGroups: ['A+','A-','B+','B-','AB+','AB-','O+','O-'],

      // Address / Leaflet state
      addressQuery: "",
      addressSuggest: [],
      addressBusy: false,
      addressDebounce: null,
      _map: null,
      _marker: null,

      // Allergy chip-picker state
      allergyTags: [],
      allergyInput: "",

      // Resend welcome state
      resendingWelcome: false,

      // Billing summary in view modal
      billingSummary: null,
      billingLoading: false,

      // Common world allergies — typed-suggestion list (drugs, foods, environmental, contact, biologic)
      commonAllergies: [
        // Drugs / medicines
        'Penicillin','Amoxicillin','Ampicillin','Cephalexin','Cefuroxime','Ceftriaxone',
        'Sulfa drugs (sulfonamides)','Cotrimoxazole (Septrin)','Tetracycline','Doxycycline',
        'Erythromycin','Azithromycin','Ciprofloxacin','Levofloxacin','Metronidazole','Vancomycin',
        'Aspirin','Ibuprofen','Diclofenac','Naproxen','Paracetamol','Codeine','Morphine','Tramadol',
        'Insulin','Heparin','Warfarin','Phenytoin','Carbamazepine','Lamotrigine','Allopurinol',
        'Iodine','Iodinated contrast media','Local anaesthetic (Lignocaine)','General anaesthetic',
        'ACE inhibitors','Beta-blockers','Statins','Chemotherapy agents','Anti-tuberculosis drugs',
        'NSAIDs (general)','Antimalarials (Sulfadoxine/Pyrimethamine)','Quinine','Vaccine component',
        // Foods
        'Peanuts','Tree nuts (almonds, cashews, walnuts)','Cow milk / dairy','Eggs','Wheat / gluten',
        'Soy','Fish','Shellfish (shrimp, lobster, crab)','Sesame','Mustard','Celery','Lupine',
        'Sulfites','Mango','Tomato','Strawberry','Banana','Avocado','Kiwi','Pineapple','Chocolate',
        // Environmental / inhalant
        'Pollen','Grass pollen','Tree pollen','Dust mites','Mould (mold) spores','Cockroach',
        'Cat dander','Dog dander','Horse dander','Smoke (tobacco)','Perfume / fragrance','Pollutants',
        // Insect / venom
        'Bee sting','Wasp sting','Hornet sting','Fire ant','Mosquito (severe reaction)',
        // Contact / chemical
        'Latex','Nickel','Chromium','Cobalt','Formaldehyde','Hair dye (PPD)','Cosmetics',
        'Detergents','Soap fragrance','Adhesives / glue','Rubber accelerators',
        // Biologic / other
        'Snake antivenom','Vaccines (specific component)','Blood / blood products','Egg-grown vaccines',
        'Sunlight (photosensitivity)','Cold (cold urticaria)','Heat','Pressure / exercise-induced',
        'No known allergies'
      ],

      // Kenya counties + sub-counties (loaded from API)
      counties: [],
      subCounties: [],

      // Kenyan insurance providers — SHA first (replaced NHIF in Oct 2024)
      insuranceProviders: [
        'SHA',
        'Britam',
        'Jubilee',
        'AAR Insurance',
        'Madison Insurance',
        'CIC Insurance',
        'UAP Old Mutual',
        'APA Insurance',
        'Heritage Insurance',
        'ICEA Lion',
        'Resolution Insurance',
        'Liberty Life',
        'Sanlam',
        'Pacis Insurance',
        'Saham Assurance',
        'First Assurance',
        'Trident Insurance',
        'Geminia Life',
        'Kenindia Assurance',
        'Other'
      ],

      // EAC + most-likely cross-border first, then a full alphabetical list
      idDocTypes: [
        { value: 'national_id',        label: 'National ID Card',      placeholder: 'e.g. 32699686' },
        { value: 'birth_certificate',  label: 'Birth Certificate',     placeholder: 'Birth cert number' },
        { value: 'passport',           label: 'Passport',              placeholder: 'e.g. AB1234567' },
        { value: 'alien_id',           label: 'Alien / Foreigner ID',  placeholder: 'Alien ID number' },
        { value: 'refugee_id',         label: 'Refugee ID',            placeholder: 'Refugee ID number' },
        { value: 'driving_licence',    label: 'Driving Licence',       placeholder: 'DL number' },
        { value: 'military_id',        label: 'Military / Service ID', placeholder: 'Service ID number' },
        { value: 'other',              label: 'Other',                 placeholder: 'Document number' }
      ],

      countries: [
        'Kenya','Uganda','Tanzania','Rwanda','Burundi','South Sudan','Ethiopia','Somalia','Democratic Republic of the Congo',
        '— — — — — — — — — — — — — — — — —',
        'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria','Azerbaijan',
        'Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso',
        'Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Costa Rica','Croatia','Cuba','Cyprus','Czechia',
        'Denmark','Djibouti','Dominica','Dominican Republic',
        'Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini',
        'Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana',
        'Haiti','Honduras','Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Ivory Coast',
        'Jamaica','Japan','Jordan','Kazakhstan','Kiribati','Kosovo','Kuwait','Kyrgyzstan',
        'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg',
        'Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar',
        'Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway',
        'Oman','Pakistan','Palau','Palestine','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal','Qatar',
        'Romania','Russia','Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','South Africa','South Korea','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland','Syria',
        'Taiwan','Tajikistan','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu',
        'Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan',
        'Vanuatu','Vatican City','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'
      ],

      branchId:   sessionStorage.getItem("branchId"),
      hospitalId: sessionStorage.getItem("hospitalId")
    };
  },
  computed: {
    todayIso() {
      return new Date().toISOString().slice(0,10);
    },
    isMinor() {
      const a = this.ageFromDob(this.form.dob);
      return typeof a === "number" && a >= 0 && a < 18;
    },
    currentIdDoc() {
      return this.idDocTypes.find(d => d.value === this.form.identification_type) || this.idDocTypes[0];
    },
    currentIdDocLabel() {
      const d = this.currentIdDoc;
      return d.label + ' Number';
    },
    currentIdDocPlaceholder() {
      return this.currentIdDoc.placeholder;
    },
    viewingIdDocLabel() {
      const t = (this.viewingPatient && this.viewingPatient.identification_type) || 'national_id';
      const d = this.idDocTypes.find(x => x.value === t);
      return d ? d.label : 'National ID';
    },
    displayAge: {
      get() {
        if (!this.form.dob) return "";
        var a = this.ageFromDob(this.form.dob);
        return (typeof a === "number" && !isNaN(a)) ? a : "";
      },
      set(val) {
        if (val === "" || val === null || val === undefined) { this.form.dob = ""; return; }
        var n = parseInt(val, 10);
        if (isNaN(n) || n < 0 || n > 130) return;
        var t = new Date();
        var y = t.getFullYear() - n;
        var mm = String(t.getMonth() + 1).padStart(2, "0");
        var dd = String(t.getDate()).padStart(2, "0");
        this.form.dob = y + "-" + mm + "-" + dd;
      }
    },
    filteredPatients() {
      var list = this.patients;
      if (this.filterGender) {
        list = list.filter(function(p) { return p.gender === this.filterGender; }.bind(this));
      }
      // status filter happens server-side via filterStatus + reload
      return list;
    },
    pagedPatients() {
      var start = (this.page - 1) * this.pageSize;
      return this.filteredPatients.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredPatients.length / this.pageSize));
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadPatients();
    this.loadCounties();
    if (this.$route.query.action === "add") {
      this.openAdd();
    }
    // Deep link: /patients?edit=78  → open the edit form pre-populated
    if (this.$route.query.edit) {
      this.openEdit({ patient_id: this.$route.query.edit });
    }
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.toastTimer)  clearTimeout(this.toastTimer);
    if (this.addressDebounce) clearTimeout(this.addressDebounce);
    this.destroyAddressMap();
  },
  watch: {
    showForm(val) {
      if (val) {
        // Pre-seed the search box from saved address (when editing)
        this.addressQuery = this.form.address || '';
        this.addressSuggest = [];
        // Pre-seed allergy chips from saved string
        this.allergyTags = (this.form.allergies || '')
          .split(',').map(s => s.trim()).filter(Boolean);
        this.allergyInput = '';
        this.$nextTick(() => this.initAddressMap());
      } else {
        this.destroyAddressMap();
        this.allergyTags = [];
        this.allergyInput = '';
      }
    },
    allergyTags: {
      deep: true,
      handler(val) {
        // Keep form.allergies in sync with the chip list
        this.form.allergies = val.join(', ');
      }
    },
    // Auto-save the registration draft to localStorage so the form survives navigation/tab-switch (FB#2-15)
    form: {
      deep: true,
      handler(val) {
        if (!this.showForm || this.editing) return;
        if (this._draftTimer) clearTimeout(this._draftTimer);
        var self = this;
        this._draftTimer = setTimeout(function(){
          try {
            if (val && (val.first_name || val.last_name || val.phone)) {
              localStorage.setItem("hms_patient_draft", JSON.stringify(val));
            }
          } catch (e) { /* ignore */ }
        }, 600);
      }
    }
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    emptyForm() {
      return {
        patient_id:           null,
        first_name:           "",
        last_name:            "",
        other_names:          "",
        dob:                  "",
        gender:               "",
        phone:                "",
        email:                "",
        national_id:          "",
        identification_type:  "national_id",
        blood_group:          "unknown",
        allergies:            "",
        chronic_conditions:   "",
        address:              "",
        country:              "Kenya",
        county_id:            null,
        sub_county_id:        null,
        county_name:          "",
        next_of_kin_name:     "",
        next_of_kin_phone:    "",
        next_of_kin_relation: "",
        payment_method:       "cash",
        insurance_provider:   "",
        insurance_number:     "",
        registration_source:  "walk_in",

        // Referral-source traceability (filled only when registration_source='referral')
        referral_source_facility:      "",
        referral_source_type:          "",
        referral_source_level:         "",
        referral_source_county:        "",
        referral_source_contact_name:  "",
        referral_source_contact_phone: "",
        referral_source_reason:        "",
        referral_arrival_condition:    "",
        referral_source_date:          "",
        referral_source_documentation: 0,
        referral_source_transport:     "",
        referral_source_notes:         "",

        // GPS coordinates of address
        latitude:                      null,
        longitude:                     null
      };
    },
    fullName(p) {
      if (!p) return "";
      return [p.first_name, p.other_names, p.last_name].filter(Boolean).join(" ").trim();
    },
    initials(p) {
      var f = (p.first_name || "")[0] || "";
      var l = (p.last_name  || "")[0] || "";
      var i = (f + l).toUpperCase();
      return i || "?";
    },
    capitalize(s) {
      if (!s) return "";
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    ageFromDob(dob) {
      if (!dob) return "—";
      var d = new Date(dob);
      if (isNaN(d.getTime())) return "—";
      var today = new Date();
      var age = today.getFullYear() - d.getFullYear();
      var m = today.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
      return age;
    },
    formatDate(d) {
      if (!d) return "—";
      try {
        var dt = new Date(d);
        if (isNaN(dt.getTime())) return d;
        return dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
      } catch(e) { return d; }
    },
    statusBadgeClass(status) {
      switch ((status || "").toLowerCase()) {
        case "active":      return "hms-badge-success";
        case "deceased":    return "hms-badge-danger";
        case "transferred": return "hms-badge-warning";
        default:            return "hms-badge-neutral";
      }
    },
    showToast(msg, isError) {
      this.toastMsg   = msg;
      this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(function(){ self.toastMsg = ""; }, 2800);
    },
    async proceedToTriage() {
      if (!this.postRegister) return;
      this.queueing = true;
      try {
        const r = await executeJson({
          function:   "queuePatientTo",
          patient_id: this.postRegister.patient_id,
          to_point:   "triage"
        }, hms);
        if (r.data && r.data.success) {
          this.showToast("Queued to triage");
        } else {
          this.showToast(r.data?.message || "Could not queue to triage", true);
        }
      } catch (e) {
        var body = e && e.response && e.response.data;
        this.showToast((body && body.message) || "Could not queue to triage", true);
      } finally {
        this.queueing = false;
        this.postRegister = null;
        this.$router.push("/triage");
      }
    },
    openQueueDialog(patient) {
      this.queueTarget = patient;
      this.queueDestination = 'triage';
      this.queueDialog = true;
    },
    closeQueueDialog() {
      this.queueDialog = false;
      this.queueTarget = null;
    },
    async confirmQueue() {
      if (!this.queueTarget || !this.queueDestination) return;
      this.queueing = true;
      try {
        const r = await executeJson({
          function:   "queuePatientTo",
          patient_id: this.queueTarget.patient_id,
          to_point:   this.queueDestination
        }, hms);
        if (r.data && r.data.success) {
          this.showToast(`Queued to ${this.queueDestination}`);
          this.closeQueueDialog();
          await this.loadPatients();
        } else {
          this.showToast(r.data?.message || "Could not queue patient", true);
        }
      } catch (e) {
        // Backend returns HTTP 409 with body when patient is already queued today —
        // surface that friendly message instead of "Network error".
        var body = e && e.response && e.response.data;
        if (body && body.message) {
          this.showToast(body.message, true);
          if (body.data && body.data.existing_ticket) {
            this.closeQueueDialog();
            await this.loadPatients();
          }
        } else {
          console.error(e);
          this.showToast("Network error", true);
        }
      } finally {
        this.queueing = false;
      }
    },
    copyText(text, label) {
      var val = (text === null || text === undefined) ? "" : String(text).trim();
      if (!val) { this.showToast("Nothing to copy", true); return; }
      var self = this;
      var done = function(){ self.showToast((label ? label + " copied: " : "Copied: ") + val); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(val).then(done).catch(function(){ self.fallbackCopy(val, done); });
      } else {
        this.fallbackCopy(val, done);
      }
    },
    fallbackCopy(val, done) {
      try {
        var ta = document.createElement("textarea");
        ta.value = val;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        done();
      } catch (e) {
        this.showToast("Copy failed — please select and copy manually", true);
      }
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      var self = this;
      this.searchTimer = setTimeout(function(){ self.loadPatients(); }, 350);
    },
    onPageSizeChange() {
      this.page = 1;
    },

    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },
    async loadCounties() {
      try {
        var r = await executeJson({ function: "listCounties" }, hms);
        if (r.data && r.data.success) this.counties = r.data.data.counties || [];
      } catch(e) { /* offline ok */ }
    },
    onIdTypeChange() {
      // When the user picks Birth Certificate for a minor, we want the auto-defaulted
      // ID number cleared so they don't accidentally submit last selection's value.
      // No-op right now beyond that; label + placeholder are computed.
    },

    async onCountyChange() {
      this.form.sub_county_id = null;
      this.subCounties = [];
      var c = this.counties.find(x => x.county_id === this.form.county_id);
      this.form.county_name = c ? c.county_name : '';
      if (!this.form.county_id) return;
      try {
        var r = await executeJson({ function: "listSubCounties", county_id: this.form.county_id }, hms);
        if (r.data && r.data.success) this.subCounties = r.data.data.sub_counties || [];
      } catch(e) { /* offline ok */ }
    },

    async loadPatients() {
      this.loading = true;
      try {
        var r;
        if (this.search && this.search.trim().length >= 2) {
          r = await executeJson({ function: "searchPatients", q: this.search.trim() }, hms);
        } else {
          r = await executeJson(
            { function: "getPatients", limit: 200, offset: 0, branch_id: this.branchId },
            hms
          );
        }
        if (r.data && r.data.success) {
          var list = (r.data.data && r.data.data.Patients) || [];
          // Client-side filter by status if API didn't honor
          if (this.filterStatus) {
            list = list.filter(function(p){ return (p.status || "").toLowerCase() === this.filterStatus; }.bind(this));
          }
          this.patients = list;
          this.page = 1;
        } else {
          this.patients = [];
        }
      } catch (e) {
        console.error("loadPatients error:", e);
        this.showToast("Failed to load patients", true);
        this.patients = [];
      }
      this.loading = false;
    },

    openAdd() {
      this.editing     = false;
      this.editingId   = null;
      this.form        = this.emptyForm();
      this.formError   = "";
      this.formSuccess = "";
      this.invalid     = {};
      this.nokOtherText = "";
      // Draft restore (FB#2-15): if a previous unfinished registration is saved locally, offer to restore.
      try {
        var raw = localStorage.getItem("hms_patient_draft");
        if (raw) {
          var d = JSON.parse(raw);
          if (d && d.first_name && confirm("Restore your unfinished patient draft (" + (d.first_name || "") + " " + (d.last_name || "") + ")?")) {
            this.form = Object.assign(this.emptyForm(), d);
          } else if (!d || !d.first_name) {
            localStorage.removeItem("hms_patient_draft");
          }
        }
      } catch (e) { /* ignore */ }
      this.showForm    = true;
    },
    async openEdit(p) {
      this.editing   = true;
      this.editingId = p.patient_id;
      this.formError   = "";
      this.formSuccess = "";
      this.invalid     = {};
      this.nokOtherText = "";

      // Open form right away pre-filled with what we have
      var prefill = function(src) {
        return {
          patient_id:           src.patient_id,
          first_name:           src.first_name || "",
          last_name:            src.last_name  || "",
          other_names:          src.other_names || "",
          dob:                  src.dob ? String(src.dob).slice(0,10) : "",
          gender:               src.gender || "",
          phone:                src.phone || "",
          email:                src.email || "",
          national_id:          src.national_id || "",
          identification_type:  src.identification_type || 'national_id',
          blood_group:          src.blood_group || "unknown",
          allergies:            src.allergies || "",
          chronic_conditions:   src.chronic_conditions || "",
          address:              src.address || "",
          country:              src.country || "Kenya",
          next_of_kin_name:     src.next_of_kin_name || "",
          next_of_kin_phone:    src.next_of_kin_phone || "",
          next_of_kin_relation: src.next_of_kin_relation || "",
          payment_method:       src.insurance_provider ? "insurance" : "cash",
          insurance_provider:   src.insurance_provider || "",
          insurance_number:     src.insurance_number || "",
          referral_source_facility:      src.referral_source_facility || "",
          referral_source_type:          src.referral_source_type || "",
          referral_source_level:         src.referral_source_level || "",
          referral_source_county:        src.referral_source_county || "",
          referral_source_contact_name:  src.referral_source_contact_name || "",
          referral_source_contact_phone: src.referral_source_contact_phone || "",
          referral_source_reason:        src.referral_source_reason || "",
          referral_arrival_condition:    src.referral_arrival_condition || "",
          referral_source_date:          src.referral_source_date ? String(src.referral_source_date).replace(' ', 'T').slice(0,16) : "",
          referral_source_documentation: Number(src.referral_source_documentation || 0),
          referral_source_transport:     src.referral_source_transport || "",
          referral_source_notes:         src.referral_source_notes || "",
          latitude:  src.latitude  != null && src.latitude  !== '' ? Number(src.latitude)  : null,
          longitude: src.longitude != null && src.longitude !== '' ? Number(src.longitude) : null
        };
      };
      this.form     = Object.assign(this.emptyForm(), prefill(p));
      this.syncNokOther();
      this.showForm = true;

      // Fetch full record in background to pull in fields not in the list query
      try {
        var r = await executeJson({ function: "getPatient", patient_id: p.patient_id }, hms);
        if (r.data && r.data.success && r.data.data && r.data.data.Patient) {
          this.form = Object.assign(this.emptyForm(), prefill(r.data.data.Patient));
          this.syncNokOther();
        }
      } catch (e) { /* keep prefill */ }
      if (this.form.county_id) await this.onCountyChange();
    },
    syncNokOther() {
      // A saved free-text relationship (from the 'Other' input) isn't in the dropdown —
      // re-select 'Other' and put the text back in the description box.
      var known = ['Mother','Father','Guardian','Grandparent','Aunt/Uncle','Sibling','Foster',
                   'Spouse','Parent','Child','Friend','Other',''];
      var rel = this.form.next_of_kin_relation || '';
      if (rel && known.indexOf(rel) === -1) {
        this.nokOtherText = rel;
        this.form.next_of_kin_relation = 'Other';
      }
    },
    closeForm() {
      this.showForm = false;
    },
    async viewPatient(p) {
      // Open immediately with what we have, then fetch full record + billing in parallel
      this.viewingPatient = p;
      this.showView = true;
      this.billingSummary = null;
      this.billingLoading = true;
      try {
        var [rPatient, rBilling] = await Promise.all([
          executeJson({ function: "getPatient",                patient_id: p.patient_id }, hms),
          executeJson({ function: "getPatientBillingSummary", patient_id: p.patient_id }, hms)
        ]);
        if (rPatient.data && rPatient.data.success && rPatient.data.data && rPatient.data.data.Patient) {
          this.viewingPatient = rPatient.data.data.Patient;
        }
        if (rBilling.data && rBilling.data.success && rBilling.data.data) {
          this.billingSummary = rBilling.data.data;
        }
      } catch (e) { /* keep summary view */ }
      this.billingLoading = false;
    },
    payLinkUrl(token) {
      if (!token) return '';
      var base = (process.env.VUE_APP_BASE_URL_HMS || 'https://rcmsuat.craftsilicon.com/api1/hms');
      return base.replace(/\/$/, '') + '/pay.php?t=' + token;
    },
    formatMoney(v) {
      if (v === null || v === undefined || v === '') return '0.00';
      return Number(v).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    payStatusBadge(s) {
      var m = { paid:'hms-badge-success', partial:'hms-badge-info', unpaid:'hms-badge-amber', waived:'hms-badge-purple', insurance_pending:'hms-badge-orange' };
      return m[s] || 'hms-badge-neutral';
    },
    confirmDelete(p) {
      this.confirmingDelete = p;
    },

    // ===== RESEND WELCOME =====
    async resendWelcome(p) {
      if (!p) return;
      if (!p.phone && !p.email) { this.showToast('No phone or email on file for this patient.', true); return; }
      this.resendingWelcome = true;
      try {
        var r = await executeJson({ function: 'resendPatientWelcome', patient_id: p.patient_id }, hms);
        if (r.data && r.data.success) {
          this.showToast(r.data.message || 'Welcome message queued for resend');
        } else {
          this.showToast((r.data && r.data.message) || 'Failed to resend.', true);
        }
      } catch(e) { this.showToast('Connection error.', true); }
      this.resendingWelcome = false;
    },

    // ===== ALLERGY CHIPS =====
    addAllergyFromInput() {
      var v = (this.allergyInput || '').trim().replace(/,$/, '').trim();
      if (!v) return;
      var exists = this.allergyTags.some(t => t.toLowerCase() === v.toLowerCase());
      if (!exists) this.allergyTags.push(v);
      this.allergyInput = '';
    },
    removeAllergy(i) {
      this.allergyTags.splice(i, 1);
    },

    // ===== ADDRESS / LEAFLET =====
    initAddressMap() {
      if (typeof window === 'undefined' || typeof window.L === 'undefined') {
        // Leaflet not loaded yet — retry once after a short delay
        setTimeout(() => { if (this.showForm) this.initAddressMap(); }, 400);
        return;
      }
      var el = this.$refs.addressMap;
      if (!el) return;
      // Destroy previous instance if any (e.g. when reopening the form)
      if (this._map) { try { this._map.remove(); } catch(e) {} this._map = null; this._marker = null; }

      var hasPin = this.form.latitude != null && this.form.longitude != null;
      var startLat = hasPin ? Number(this.form.latitude)  : -1.0833;  // Narok default
      var startLng = hasPin ? Number(this.form.longitude) :  35.8667;
      var startZoom = hasPin ? 16 : 12;

      var L = window.L;
      this._map = L.map(el, { zoomControl: true }).setView([startLat, startLng], startZoom);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors'
      }).addTo(this._map);

      this._marker = L.marker([startLat, startLng], { draggable: true }).addTo(this._map);
      var self = this;
      this._marker.on('dragend', function(ev) {
        var ll = ev.target.getLatLng();
        self.form.latitude  = +ll.lat.toFixed(7);
        self.form.longitude = +ll.lng.toFixed(7);
      });
      this._map.on('click', function(ev) {
        self._marker.setLatLng(ev.latlng);
        self.form.latitude  = +ev.latlng.lat.toFixed(7);
        self.form.longitude = +ev.latlng.lng.toFixed(7);
      });
      if (hasPin) {
        this.form.latitude  = +Number(this.form.latitude).toFixed(7);
        this.form.longitude = +Number(this.form.longitude).toFixed(7);
      }
      // Fix sizing if container was hidden before
      setTimeout(() => { try { this._map.invalidateSize(); } catch(e) {} }, 250);
    },
    destroyAddressMap() {
      if (this._map) { try { this._map.remove(); } catch(e) {} this._map = null; this._marker = null; }
      this.addressSuggest = [];
    },
    setMapPin(lat, lng, zoom) {
      if (!this._map) return;
      this._map.setView([lat, lng], zoom || 16);
      if (!this._marker) {
        this._marker = window.L.marker([lat, lng], { draggable: true }).addTo(this._map);
      } else {
        this._marker.setLatLng([lat, lng]);
      }
      this.form.latitude  = +Number(lat).toFixed(7);
      this.form.longitude = +Number(lng).toFixed(7);
    },
    onAddressInput() {
      if (this.addressDebounce) clearTimeout(this.addressDebounce);
      if (!this.addressQuery || this.addressQuery.length < 3) { this.addressSuggest = []; return; }
      this.addressDebounce = setTimeout(() => { this.geocodeAddress(true); }, 500);
    },
    async geocodeAddress(suggestOnly) {
      var q = (this.addressQuery || '').trim();
      if (!q) { this.addressSuggest = []; return; }
      this.addressBusy = !suggestOnly;
      try {
        // OpenStreetMap Nominatim — free, no key required; restrict to Kenya by default
        var url = 'https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=ke&q=' + encodeURIComponent(q);
        var resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
        var data = await resp.json();
        if (Array.isArray(data) && data.length) {
          if (suggestOnly) {
            this.addressSuggest = data;
          } else {
            this.pickAddressSuggestion(data[0]);
          }
        } else if (!suggestOnly) {
          this.showToast('No matches — drop a pin on the map directly.', true);
        }
      } catch (e) {
        if (!suggestOnly) this.showToast('Geocoder unreachable — drop a pin manually.', true);
      }
      this.addressBusy = false;
    },
    pickAddressSuggestion(s) {
      this.addressSuggest = [];
      this.addressQuery = s.display_name;
      this.form.address = s.display_name;
      var lat = parseFloat(s.lat); var lng = parseFloat(s.lon);
      this.setMapPin(lat, lng, 16);
    },
    useMyLocation() {
      if (!navigator.geolocation) { this.showToast('Geolocation not supported by browser.', true); return; }
      this.addressBusy = true;
      navigator.geolocation.getCurrentPosition((pos) => {
        this.setMapPin(pos.coords.latitude, pos.coords.longitude, 17);
        this.addressBusy = false;
      }, (err) => {
        this.showToast('Could not get GPS: ' + err.message, true);
        this.addressBusy = false;
      }, { enableHighAccuracy: true, timeout: 10000 });
    },

    onPaymentMethodChange() {
      // Clear insurance details when not paying by insurance
      if (this.form.payment_method !== 'insurance') {
        this.form.insurance_provider = "";
        this.form.insurance_number = "";
      }
    },

    async savePatient() {
      this.formError   = "";
      this.formSuccess = "";
      this.invalid     = {};

      // Field-specific validation (HMIS_Issues: name the exact missing field, mark it red)
      var missing = [];
      if (!this.form.first_name) { this.invalid.first_name = true; missing.push("First Name"); }
      if (!this.form.last_name)  { this.invalid.last_name  = true; missing.push("Last Name"); }
      if (!this.form.dob)        { this.invalid.dob        = true; missing.push("Date of Birth"); }
      if (!this.form.gender)     { this.invalid.gender     = true; missing.push("Gender"); }
      if (!this.editing && !(this.form.phone || '').trim()) { this.invalid.phone = true; missing.push("Phone"); }
      if (this.isMinor) {
        if (!this.form.next_of_kin_name)     { this.invalid.next_of_kin_name     = true; missing.push("Parent/Guardian Name"); }
        if (!this.form.next_of_kin_phone)    { this.invalid.next_of_kin_phone    = true; missing.push("Parent/Guardian Phone"); }
        if (!this.form.next_of_kin_relation) { this.invalid.next_of_kin_relation = true; missing.push("Parent/Guardian Relationship"); }
      }
      if (this.form.next_of_kin_relation === 'Other' && !(this.nokOtherText || '').trim()) {
        this.invalid.nok_other = true; missing.push("Relationship description");
      }
      if (this.form.registration_source === 'referral') {
        if (!this.form.referral_source_facility)  { this.invalid.referral_source_facility  = true; missing.push("Referring Facility Name"); }
        if (!this.form.referral_source_type)      { this.invalid.referral_source_type      = true; missing.push("Referring Facility Type"); }
        if (!this.form.referral_arrival_condition){ this.invalid.referral_arrival_condition= true; missing.push("Arrival Condition"); }
      }
      if (missing.length) {
        this.formError = "Please fill in: " + missing.join(", ") + ". The missing fields are highlighted in red.";
        this.scrollErrorIntoView();
        return;
      }

      this.processing = true;
      try {
        var fnName  = this.editing ? "updatePatient" : "registerPatient";
        var payload = Object.assign({ function: fnName }, this.form);
        if (this.form.next_of_kin_relation === 'Other' && (this.nokOtherText || '').trim()) {
          payload.next_of_kin_relation = this.nokOtherText.trim();
        }
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.formSuccess = r.data.message || (this.editing ? "Updated" : "Registered");
          this.showToast(this.editing ? "Patient updated" : "Patient registered");
          this.showForm = false;
          try { localStorage.removeItem("hms_patient_draft"); } catch (e) {}
          if (!this.editing && r.data.data && r.data.data.patient_id) {
            this.postRegister = {
              patient_id: r.data.data.patient_id,
              patient_no: r.data.data.patient_no || '',
              name: (this.form.first_name + ' ' + this.form.last_name).trim()
            };
          }
          this.loadPatients();
        } else {
          this.formError = r.data.message || "Operation failed.";
          this.scrollErrorIntoView();
        }
      } catch (e) {
        console.error(e);
        this.formError = "Connection error. Please try again.";
        this.scrollErrorIntoView();
      }
      this.processing = false;
    },
    scrollErrorIntoView() {
      this.$nextTick(() => {
        var el = this.$refs.submitError;
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },

    async doDelete() {
      if (!this.confirmingDelete) return;
      this.deletingId = this.confirmingDelete.patient_id;
      try {
        var r = await executeJson(
          { function: "deletePatient", patient_id: this.confirmingDelete.patient_id },
          hms
        );
        if (r.data && r.data.success) {
          this.showToast("Patient deleted");
          this.confirmingDelete = null;
          this.loadPatients();
        } else {
          this.showToast(r.data.message || "Delete failed", true);
        }
      } catch (e) {
        console.error(e);
        this.showToast("Connection error", true);
      }
      this.deletingId = null;
    }
  }
};
</script>
